import { Button, DatePicker, Form, Input, InputNumber, Select, Typography, Divider } from "antd";
import dayjs from "dayjs";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { loadAllStaff } from "../../redux/rtk/features/user/userSlice";

const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

const AddPromotionCycle = ({ form, onSubmit }) => {
	const dispatch = useDispatch();
	const [loader, setLoader] = React.useState(false);
	const staffList = useSelector((state) => state.users?.list || []);

	useEffect(() => {
		dispatch(loadAllStaff({ status: true }));
	}, [dispatch]);

	const onFinish = async (values) => {
		try {
			setLoader(true);
			const cycleData = {
				...values,
				startDate: dayjs(values.startDate).format("YYYY-MM-DD"),
				endDate: dayjs(values.endDate).format("YYYY-MM-DD"),
				announcementDate: values.announcementDate ? dayjs(values.announcementDate).format("YYYY-MM-DD") : null,
				status: values.status || "Planning",
			};

			if (onSubmit) {
				onSubmit(cycleData);
				toast.success("Promotion Cycle Created Successfully!");
			}
		} catch (error) {
			toast.error("Failed to create promotion cycle");
		} finally {
			setLoader(false);
		}
	};

	const onFinishFailed = (errorInfo) => {
		toast.warning("Please fill all required fields");
		setLoader(false);
	};

	return (
		<div className="column-design border rounded card-custom">
			<Title level={4} className="m-2 mt-5 mb-5 text-center">
				Promotion Cycle Management
			</Title>
			<Form
				form={form}
				style={{ marginBottom: "40px" }}
				name="promotion-cycle-form"
				labelCol={{ span: 8 }}
				wrapperCol={{ span: 16 }}
				onFinish={onFinish}
				onFinishFailed={onFinishFailed}
				autoComplete="off"
				preserve={false}
			>
				<Form.Item
					label={<span className="required-label">Cycle Name</span>}
					name="cycleName"
					rules={[
						{ required: true, message: "Please enter cycle name!" },
					]}
				>
					<Input placeholder="e.g., Q1 2024, Annual 2024" />
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Cycle Type</span>}
					name="cycleType"
					rules={[
						{ required: true, message: "Please select cycle type!" },
					]}
				>
					<Select placeholder="Select cycle type">
						<Option value="Quarterly">Quarterly</Option>
						<Option value="Semi-Annual">Semi-Annual</Option>
						<Option value="Annual">Annual</Option>
						<Option value="Ad-hoc">Ad-hoc</Option>
					</Select>
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Start Date</span>}
					name="startDate"
					rules={[
						{ required: true, message: "Please select start date!" },
					]}
				>
					<DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
				</Form.Item>

				<Form.Item
					label={<span className="required-label">End Date</span>}
					name="endDate"
					rules={[
						{ required: true, message: "Please select end date!" },
					]}
				>
					<DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
				</Form.Item>

				<Form.Item
					label="Announcement Date"
					name="announcementDate"
				>
					<DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
				</Form.Item>

				<Form.Item
					label={<span className="required-label">Status</span>}
					name="status"
					initialValue="Planning"
				>
					<Select placeholder="Select status">
						<Option value="Planning">Planning</Option>
						<Option value="Active">Active</Option>
						<Option value="Completed">Completed</Option>
						<Option value="Cancelled">Cancelled</Option>
					</Select>
				</Form.Item>

				<Form.Item
					label="Budget Allocation"
					name="budgetAllocation"
				>
					<InputNumber
						style={{ width: "100%" }}
						prefix="₹"
						placeholder="Enter budget amount"
						min={0}
						formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
						parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
					/>
				</Form.Item>

				<Form.Item
					label="Eligible Employees"
					name="eligibleEmployees"
					tooltip="Number of employees eligible for promotion"
				>
					<InputNumber
						style={{ width: "100%" }}
						placeholder="Enter number of eligible employees"
						min={0}
					/>
				</Form.Item>

				<Divider>Promotion Criteria</Divider>

				<Form.Item
					label="Minimum Tenure (Months)"
					name="minTenureMonths"
				>
					<InputNumber
						style={{ width: "100%" }}
						placeholder="Minimum tenure required"
						min={0}
					/>
				</Form.Item>

				<Form.Item
					label="Minimum Performance Rating"
					name="minPerformanceRating"
				>
					<InputNumber
						style={{ width: "100%" }}
						placeholder="Minimum performance rating (1-5)"
						min={1}
						max={5}
						step={0.1}
					/>
				</Form.Item>

				<Form.Item
					label="Required Skills Count"
					name="requiredSkillsCount"
				>
					<InputNumber
						style={{ width: "100%" }}
						placeholder="Minimum number of skills required"
						min={0}
					/>
				</Form.Item>

				<Form.Item
					label="Promotion Criteria Details"
					name="promotionCriteria"
				>
					<TextArea
						rows={3}
						placeholder="Enter detailed promotion criteria, requirements, and evaluation process"
					/>
				</Form.Item>

				<Form.Item
					label="Notes"
					name="notes"
				>
					<TextArea
						rows={3}
						placeholder="Additional notes or comments about this promotion cycle"
					/>
				</Form.Item>

				<Form.Item wrapperCol={{ offset: 8, span: 16 }}>
					<Button
						type="primary"
						htmlType="submit"
						loading={loader}
						block
						className="btn btn-primary"
					>
						Submit
					</Button>
				</Form.Item>
			</Form>
		</div>
	);
};

export default AddPromotionCycle;

